import React, { useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { Button, Dialog } from "iuvity-ui-components";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { CustomInput } from "../inputs/CustomInput";
import { EnvCreatorSchema } from "../../validations/schemas";
import { MultiContentDialogProps } from "../dialog/MultiContentDialog";
import { DialogProps } from "../dialog/CustomDialog";
import { ENVIRONMENT_STATES, REVALIDATE_FORM_PROPS } from "../../constants";
import { DropdownOptions } from "../../interfaces";
import { AppContext } from "../../context";
import { useEnvironments } from "../../hooks/useEnvironments";

interface EnvCreatorProps {
  options: DropdownOptions;
  update: boolean;
  createFn: Function;
  reloadFn: Function;
  modalController: React.Dispatch<
    React.SetStateAction<MultiContentDialogProps>
  >;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
  env?: any
}

export const envCreatorDefaultValues = {
  id: "",
  name: "",
  agentid: "",
};

export const EnvCreator: React.FC<EnvCreatorProps> = ({
  options,
  update,
  createFn,
  reloadFn,
  modalController,
  auxModalController,
  env
}) => {
  const {
    t,
    appState: { user, selectedApplication, selectedEnvironment },
  } = useContext(AppContext);

  const {
    control,
    getValues,
    reset,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });

  const { envAction } = useEnvironments("DATA");

  const onCancel = () => {
    document.body.style.overflowY = "auto";
    modalController((prev) => ({
      ...prev,
      children: null,
    }));
    reset(envCreatorDefaultValues);
  };

  const onCreateApp = async () => {
    const formValues = getValues();
    onCancel();
    const actionStatus = formValues?.status === 'DELETED'
      ? await envAction("DELETE", env.id)
      : await createFn(update ? "UPDATE" : "CREATE", [
        {
          ...formValues,
          [update ? "updatedby" : "createdby"]: user?.name,
          applicationid: selectedApplication?.id,
          tenantid: selectedApplication?.tenantid,
        },
      ]);
    if (actionStatus === "SUCCESS") {
      reloadFn();
    }
  };

  const handleModalProps = () => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: update
        ? t(`Environments.updateModalTitle`)
        : t(`Environments.createModalTitle`),
      content: update
        ? `${t(`Environments.updateModalContent`)}`
        : t(`Environments.createModalContent`),
      onConfirm: onCreateApp,
    }));
  };

  useEffect(() => {
    if (update && selectedEnvironment) {
      reset({
        id: selectedEnvironment?.id,
        name: selectedEnvironment?.name,
        // agentid: selectedEnvironment?.agentid,
        agentid: options.find((env) => env.value === selectedEnvironment?.agentid) ? selectedEnvironment?.agentid : "",
        status: selectedEnvironment?.status,
        cacheurl: selectedEnvironment?.cacheurl,
        cacheapikey: selectedEnvironment?.cacheapikey,
      });
    } else {
      reset(envCreatorDefaultValues);
    }
  }, [reset, selectedEnvironment, update, options]);

  const renderFooter = () => {
    return (
      <div className="flex align-items-center justify-content-flex-end">
        <Button
          label={t("Environments.cancelBtnLabel")}
          onClick={onCancel}
          className="mr-4"
          variant="p-button-link"
        />
        <Button
          label={
            update
              ? t("Environments.saveBtnLabel")
              : t("Environments.createBtnLabel")
          }
          variant="p-button"
          onClick={handleModalProps}
          disabled={update ? !(isValid && isDirty) : !isValid}
        />
      </div>
    );
  };

  return (
    <Dialog
      className="dialog-form"
      header={
        update
          ? t("Environments.editEnvironmentTitle")
          : t("Environments.newEnvironmentTitle")
      }
      visible={true}
      onHide={onCancel}
      style={{ width: "25rem" }}
      footer={renderFooter}
    >
      <div className="form sm">
        <p className="pb-4 description-new-environment">
          {!update ? t("Environments.descriptionNewEnvironment") : t("Environments.descriptionUpdateEnvironment")}
        </p>
        {update && (
          <div className='app-fields' style={{ marginLeft: '1.5rem' }}>
            <CustomInput
              isReadOnly
              id="input-name-env"
              name={undefined}
              className="mb-3 input-name-env"
              label={t(`Environments.nameLabel`)}
              value={selectedEnvironment?.name}
              placeholder={''}
              type="text"
              autoComplete="off"
            />
          </div>
        )}
        {!update &&
          <div className='app-fields' style={{ marginLeft: '1.5rem' }}>
            <Controller
              key="name"
              name="name"
              control={control}
              rules={EnvCreatorSchema(t).name}
              render={({ field }) => {
                return (
                  <CustomInput
                    id="input-name-env"
                    className="mb-3 input-name-env"
                    label={t(`Environments.nameLabel`)}
                    placeholder={t(`Environments.namePlaceholder`)}
                    type="text"
                    autoComplete="off"
                    keyfilter={EnvCreatorSchema(t)?.name?.keyfilter}
                    maxLength={EnvCreatorSchema(t)?.name?.maxLength?.value}
                    {...field}
                  />
                );
              }}
            />
            <ErrorMessage
              errors={errors}
              name="name"
              //@ts-ignore
              render={({ message }) => (
                <small
                  className="p-error block"
                  style={{ marginTop: "-0.5rem", marginBottom: "1rem" }}
                >
                  {message}
                </small>
              )}
            />
          </div>
        }

        <Controller
          key="agent"
          name="agentid"
          control={control}
          rules={EnvCreatorSchema(t).agent}
          render={({ field }) => {
            return (
              // @ts-ignore
              <div className='app-fields' style={{ marginLeft: '1.5rem' }}>
                <Dropdown
                  id="im-type-dropdown"
                  className="dropdown mb-3 input-clone-fields"
                  label={t(`Environments.agentnameLabel`)}
                  placeholder={t(`Environments.agentnamePlaceholder`)}
                  options={options}
                  filter={false}
                  style={{
                    zIndex: "2300 !important",
                  }}
                  hasTooltipRef
                  tooltipRef={{
                    content: t(`Environments.agentTooltip`),
                    position: "bottom",
                    id: "dropdown-type",
                  }}
                  tooltipIdName="dropdown-type"
                  {...field}
                  showClear={field.value ? true : false}
                  onChange={(e: any) => {
                    setValue(field.name, e.value || null, REVALIDATE_FORM_PROPS);
                  }}
                  //@ts-ignore
                  className={errors['agentid']?.message ? "p-invalid dropdown mb-3 input-clone-fields " : "dropdown mb-3 input-clone-fields"}
                />
              </div>
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="agentid"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "-0.5rem", marginBottom: "1rem", marginLeft: '1.5rem' }}
            >
              {message}
            </small>
          )}
        />

        {update && (
          <div>
            <Controller
              key="status"
              name="status"
              control={control}
              rules={EnvCreatorSchema(t).status}
              render={({ field }) => {
                return (
                  // @ts-ignore
                  <div className='app-fields' style={{ marginLeft: '1.5rem' }}>
                    <Dropdown
                      label={t(`Applications.statusLabel`)}
                      placeholder={t(`Applications.statusPlaceholder`)}
                      optionLabel="label"
                      optionValue="value"
                      options={ENVIRONMENT_STATES(t)}
                      filter={false}
                      style={{
                        zIndex: "6000 !important",
                      }}
                      {...field}
                      showClear={field.value ? true : false}
                      onChange={(e: any) => {
                        setValue(field.name, e.value || null, REVALIDATE_FORM_PROPS);
                      }}
                      className={errors['status']?.message ? "p-invalid dropdown mb-3 input-clone-fields " : "dropdown mb-3 input-clone-fields"}

                    />
                  </div>
                );
              }}
            />
            <ErrorMessage
              errors={errors}
              name="status"
              //@ts-ignore
              render={({ message }) => (
                <small
                  className="p-error block"
                  style={{ marginTop: "-0.5rem", marginBottom: "1rem", marginLeft: '1.5rem' }}
                >
                  {message}
                </small>
              )}
            />
          </div>
        )

        }

        {/* CacheURL field */}
        <div className='app-fields' style={{ marginLeft: '1.5rem' }}>
          <Controller
            key="cacheurl"
            name="cacheurl"
            control={control}
            rules={EnvCreatorSchema(t).cacheurl}
            render={({ field }) => {
              return (
                <CustomInput
                  id="input-cacheurl-env"
                  className="mb-3 input-cacheurl-env"
                  label={t(`Environments.cacheurlLabel`)}
                  placeholder={t(`Environments.cacheurlPlaceholder`)}
                  type="text"
                  autoComplete="off"
                  maxLength={EnvCreatorSchema(t)?.cacheurl?.maxLength?.value}
                  hasTooltip
                  tooltipText={t(`Environments.cacheurlTooltip`)}
                  {...field}
                />
              );
            }}
          />
          <ErrorMessage
            errors={errors}
            name="cacheurl"
            //@ts-ignore
            render={({ message }) => (
              <small
                className="p-error block"
                style={{ marginTop: "-0.5rem", marginBottom: "1rem" }}
              >
                {message}
              </small>
            )}
          />
        </div>

        {/* CacheApikey field */}
        <div className='app-fields' style={{ marginLeft: '1.5rem' }}>
          <Controller
            key="cacheapikey"
            name="cacheapikey"
            control={control}
            rules={EnvCreatorSchema(t).cacheapikey}
            render={({ field }) => {
              return (
                <CustomInput
                  id="input-cacheapikey-env"
                  className="mb-3 input-cacheapikey-env"
                  label={t(`Environments.cacheapikeyLabel`)}
                  placeholder={t(`Environments.cacheapikeyPlaceholder`)}
                  type="text"
                  autoComplete="off"
                  maxLength={EnvCreatorSchema(t)?.cacheapikey?.maxLength?.value}
                  hasTooltip
                  tooltipText={t(`Environments.cacheapikeyTooltip`)}
                  {...field}
                />
              );
            }}
          />
          <ErrorMessage
            errors={errors}
            name="cacheapikey"
            //@ts-ignore
            render={({ message }) => (
              <small
                className="p-error block"
                style={{ marginTop: "-0.5rem", marginBottom: "1rem" }}
              >
                {message}
              </small>
            )}
          />
        </div>
      </div>
    </Dialog>
  );
};
